import styled from 'styled-components'
import React, { useEffect, } from 'react'

import { InfiniteScrollbar, ControlRoomRequestingOrderSummary, } from '@components'

import { CONTROL_ROOM_REQUESTING_POLLING_INTERVAL_MS, } from '@globals'

import { useStatePagination, } from '@utils'

import { TypePaginatableAction, ControlRoomRequestingOrder, } from '@types'

interface Props {
  title: string
  description?: string
  emptyDescription: string
  count?: number
  countSuffix?: string
  emphasisCount?: number
  emphasisLabel?: string
  className?: string
  reduxAction: TypePaginatableAction
  successState: string
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
  height: 100%;

  .header {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    padding: 15px 16px 14px;
    background-color: rgb(58, 69, 84);
    color: white;
  }

  .title-container {
    display: flex;
    flex-direction: column;
    gap: 7px;
  }

  h2 {
    font-size: 20px;
  }

  .description {
    font-size: 12px;
    color: rgba(255, 255, 255, 0.75);
    white-space: nowrap;
  }

  .count-container {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    align-content: flex-start;
    gap: 8px;
  }

  .count-badge {
    border-radius: 999px;
    padding: 5px 10px;
    font-size: 12px;
    font-weight: 700;
    background-color: rgba(255, 255, 255, 0.18);
  }

  .count-badge.is-emphasis {
    background-color: rgb(255, 226, 230);
    color: rgb(148, 0, 33);
  }

  .empty-state {
    padding: 28px 20px;
    color: rgb(98, 111, 126);
    text-align: center;
    line-height: 1.6;
  }

  .loading-list {
    padding: 12px 14px 16px;
  }

  .loading-card {
    border-radius: 12px;
    padding: 14px 14px 12px;
    background-color: rgba(255, 255, 255, 0.82);
    border: 1px solid rgba(226, 232, 240, 0.9);
    margin-bottom: 10px;
  }

  .loading-line {
    border-radius: 999px;
    background: linear-gradient(90deg, rgba(226, 232, 240, 0.75) 0%, rgba(241, 245, 249, 1) 50%, rgba(226, 232, 240, 0.75) 100%);
    background-size: 200% 100%;
    animation: loading-shimmer 1.4s ease-in-out infinite;
  }

  .loading-line + .loading-line {
    margin-top: 10px;
  }

  .loading-line.title {
    width: 42%;
    height: 16px;
  }

  .loading-line.badges {
    width: 36%;
    height: 14px;
  }

  .loading-line.body {
    width: 100%;
    height: 13px;
  }

  .loading-line.body.short {
    width: 72%;
  }

  @keyframes loading-shimmer {
    0% {
      background-position: 200% 0;
    }

    100% {
      background-position: -200% 0;
    }
  }
`

function ControlRoomRequestingList(props: Props) {
  const {
    title, description, emptyDescription, count, countSuffix, emphasisCount,
    emphasisLabel, className, reduxAction, successState,
  } = props

  const {
    data, loadMore, refresh, isMoreExists, isInitialLoadDone,
  } = useStatePagination<ControlRoomRequestingOrder>({
    reduxAction,
    resultKey: 'results',
    successState,
    limit: 20,
  })

  useEffect(
    () => {
      const interval = setInterval(
        () => refresh(),
        CONTROL_ROOM_REQUESTING_POLLING_INTERVAL_MS
      )

      return () => clearInterval(interval)
    },
    [ refresh, ]
  )

  return (
    <Container className={ className }>
      <div className='header'>
        <div className='title-container'>
          <h2>{ title }</h2>
          {
            description && (
              <span className='description'>{ description }</span>
            )
          }
        </div>
        <div className='count-container'>
          {
            typeof count === 'number' && (
              <span className='count-badge'>
                { count }{ countSuffix || '건' }
              </span>
            )
          }
          {
            typeof emphasisCount === 'number' && emphasisCount > 0 && emphasisLabel && (
              <span className='count-badge is-emphasis'>
                { emphasisLabel } { emphasisCount }건
              </span>
            )
          }
        </div>
      </div>
      <InfiniteScrollbar onEndReached={ () => isMoreExists ? loadMore() : null }>
        {
          !isInitialLoadDone ? (
            <div className='loading-list'>
              {
                Array.from({ length: 4, }).map((_, index) => (
                  <div className='loading-card' key={ `requesting-loading-${ index }` }>
                    <div className='loading-line title' />
                    <div className='loading-line badges' />
                    <div className='loading-line body' />
                    <div className='loading-line body short' />
                    <div className='loading-line body' />
                  </div>
                ))
              }
            </div>
          ) : data?.results?.length ? (
            <div>
              {
                data.results.map(order => (
                  <ControlRoomRequestingOrderSummary
                    key={ `control-room-requesting-order-${ order.id }` }
                    order={ order }
                  />
                ))
              }
            </div>
          ) : (
            <div className='empty-state'>
              { emptyDescription }
            </div>
          )
        }
      </InfiniteScrollbar>
    </Container>
  )
}

export default styled(ControlRoomRequestingList)``
